import {
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import type { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { parse } from 'date-fns';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { LuClock } from 'react-icons/lu';

const HOURS = Array.from({ length: 24 }, (_, index) => index);
const MINUTES = Array.from({ length: 60 }, (_, index) => index);

// Where an empty hour column opens, so the common daytime range needs little
// scroll. Minutes already open on 00.
const DEFAULT_HOUR = 12;

const pad = (value: number) => String(value).padStart(2, '0');

interface TimeColumnProps {
  label: string;
  options: number[];
  selected: number | null;
  /** Centered on open when nothing is selected. */
  anchor?: number;
  onSelect: (value: number) => void;
  visible: boolean;
  /** Lets the parent reach the selected option, e.g. to focus it. */
  selectedRef?: RefObject<HTMLButtonElement>;
  anchorRef?: RefObject<HTMLButtonElement>;
}

const TimeColumn = ({
  label,
  options,
  selected,
  anchor,
  onSelect,
  visible,
  selectedRef: externalSelectedRef,
  anchorRef: externalAnchorRef,
}: TimeColumnProps) => {
  const labelId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const ownSelectedRef = useRef<HTMLButtonElement>(null);
  const selectedRef = externalSelectedRef ?? ownSelectedRef;
  const ownAnchorRef = useRef<HTMLButtonElement>(null);
  const anchorRef = externalAnchorRef ?? ownAnchorRef;

  // The panel mounts in a portal, so the selected row can only be centered
  // once the list has been laid out.
  useEffect(() => {
    if (!visible) return;
    const frame = requestAnimationFrame(() => {
      const list = listRef.current;
      const item = selectedRef.current ?? anchorRef.current;
      if (!list || !item) return;
      const offset =
        item.getBoundingClientRect().top - list.getBoundingClientRect().top;
      list.scrollTop += offset - (list.clientHeight - item.offsetHeight) / 2;
    });
    return () => cancelAnimationFrame(frame);
  }, [visible]);

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="flex min-w-0 flex-1 flex-col gap-1"
    >
      <p
        id={labelId}
        className="text-2xs font-medium uppercase tracking-[0.02em] text-gray-400 text-center"
      >
        {label}
      </p>
      <div
        ref={listRef}
        data-slot="timepicker-list"
        className="h-56 overflow-y-auto overscroll-contain rounded-lg border border-gray-200"
      >
        <div className="flex flex-col gap-1 p-1">
          {options.map((option) => {
            const isSelected = selected === option;
            return (
              <button
                key={option}
                ref={
                  isSelected
                    ? selectedRef
                    : option === anchor
                    ? anchorRef
                    : undefined
                }
                type="button"
                aria-label={`${label} ${pad(option)}`}
                aria-pressed={isSelected}
                onClick={() => onSelect(option)}
                className={cn(
                  'h-12 shrink-0 cursor-pointer rounded-lg text-base font-medium transition-colors',
                  isSelected
                    ? 'bg-indigo-700 text-white'
                    : 'text-indigo-900 hover:bg-blue-50 active:bg-blue-100',
                )}
              >
                {pad(option)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface TimePickerPanelProps {
  hour: number | null;
  minute: number | null;
  visible: boolean;
  selectedHourRef: RefObject<HTMLButtonElement>;
  anchorHourRef: RefObject<HTMLButtonElement>;
  canClear: boolean;
  showNow: boolean;
  onSelectHour: (value: number) => void;
  onSelectMinute: (value: number) => void;
  onClear: () => void;
  onNow: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}

const TimePickerPanel = ({
  hour,
  minute,
  visible,
  selectedHourRef,
  anchorHourRef,
  canClear,
  showNow,
  onSelectHour,
  onSelectMinute,
  onClear,
  onNow,
  onCancel,
  onConfirm,
}: TimePickerPanelProps) => (
  <>
    {/* Visual echo only: the pressed option in each column already tells a
        screen reader what is picked, and "08:--" reads out as noise. */}
    <p
      aria-hidden="true"
      className="mb-2 text-center text-lg font-semibold tabular-nums"
    >
      <span className={hour === null ? 'text-gray-400' : 'text-indigo-900'}>
        {hour === null ? '--' : pad(hour)}
      </span>
      <span
        className={
          hour === null || minute === null ? 'text-gray-400' : 'text-indigo-900'
        }
      >
        :
      </span>
      <span className={minute === null ? 'text-gray-400' : 'text-indigo-900'}>
        {minute === null ? '--' : pad(minute)}
      </span>
    </p>
    <div className="flex gap-3">
      <TimeColumn
        label="Heure"
        options={HOURS}
        selected={hour}
        anchor={DEFAULT_HOUR}
        onSelect={onSelectHour}
        visible={visible}
        selectedRef={selectedHourRef}
        anchorRef={anchorHourRef}
      />
      <TimeColumn
        label="Minute"
        options={MINUTES}
        selected={minute}
        onSelect={onSelectMinute}
        visible={visible}
      />
    </div>
    {showNow && (
      <Button
        variant="outline"
        size="md"
        onClick={onNow}
        className="mt-3 min-h-11 w-full text-base"
      >
        Maintenant
      </Button>
    )}
    <div className="mt-4 flex items-center justify-between gap-1">
      <Button
        variant="ghost"
        size="md"
        onClick={onClear}
        disabled={!canClear}
        className="min-h-11 px-3 text-base text-gray-700"
      >
        Effacer
      </Button>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="md"
          onClick={onCancel}
          className="min-h-11 px-3 text-base text-gray-700"
        >
          Annuler
        </Button>
        <Button
          variant="ghost"
          size="md"
          onClick={onConfirm}
          disabled={hour === null || minute === null}
          className="min-h-11 bg-indigo-700 px-4 text-base text-white hover:bg-indigo-800 active:bg-indigo-900"
        >
          Valider
        </Button>
      </div>
    </div>
  </>
);

interface TimePickerProps {
  value?: Date | null;
  onChange?: (date: Date | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
  title?: string;
  description?: string;
  /** Offers a one-tap "Maintenant" that commits the current time. */
  showNow?: boolean;
  isError?: boolean;
  className?: string;
}

export function TimePicker({
  value,
  onChange,
  disabled,
  placeholder = '--:--',
  title = 'Choisir une heure',
  description,
  showNow = false,
  isError,
  className,
}: TimePickerProps) {
  const [open, setOpen] = useState(false);

  const [hour, setHour] = useState<number | null>(null);
  const [minute, setMinute] = useState<number | null>(null);

  const selectedHourRef = useRef<HTMLButtonElement>(null);
  const anchorHourRef = useRef<HTMLButtonElement>(null);
  // Focusing the first row by default would scroll the hour column back to 00.
  const initialFocus = useCallback(
    () => selectedHourRef.current ?? anchorHourRef.current ?? true,
    [],
  );

  const commit = useCallback(
    (nextHour: number, nextMinute: number) => {
      onChange?.(parse(`${pad(nextHour)}:${pad(nextMinute)}`, 'HH:mm', 0));
    },
    [onChange],
  );

  const onOpenChange = useCallback(
    (
      nextOpen: boolean,
      { reason }: DialogPrimitive.Root.ChangeEventDetails,
    ) => {
      if (!nextOpen) {
        if (reason === 'escape-key') setOpen(false);
        return;
      }
      setHour(value?.getHours() ?? null);
      setMinute(value?.getMinutes() ?? null);
      setOpen(true);
    },
    [value],
  );

  const onConfirm = useCallback(() => {
    if (hour === null || minute === null) return;
    commit(hour, minute);
    setOpen(false);
  }, [hour, minute, commit]);

  const onNow = useCallback(() => {
    const now = new Date();
    commit(now.getHours(), now.getMinutes());
    setOpen(false);
  }, [commit]);

  const onCancel = useCallback(() => setOpen(false), []);

  const onClear = useCallback(() => {
    onChange?.(undefined);
    setOpen(false);
  }, [onChange]);

  const formattedTime = value
    ? `${pad(value.getHours())}:${pad(value.getMinutes())}`
    : undefined;

  const panel = (
    <TimePickerPanel
      hour={hour}
      minute={minute}
      visible={open}
      selectedHourRef={selectedHourRef}
      anchorHourRef={anchorHourRef}
      canClear={hour !== null || minute !== null || Boolean(value)}
      showNow={showNow}
      onSelectHour={setHour}
      onSelectMinute={setMinute}
      onClear={onClear}
      onNow={onNow}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange} disablePointerDismissal>
      <DialogTrigger
        disabled={disabled}
        // Otherwise an empty field is announced as "--:--".
        aria-label={`${description ? `${title}, ${description}` : title} : ${
          formattedTime ?? 'aucune heure choisie'
        }`}
        className={cn(
          'h-12 rounded-1.5xl inline-flex w-full cursor-pointer items-center justify-start gap-2 border px-4 py-2 text-left font-medium disabled:pointer-events-none disabled:opacity-50',
          isError
            ? 'border-red-300 bg-red-25 text-red-600'
            : open
            ? 'border-indigo-900 bg-white'
            : value
            ? 'bg-blue-50'
            : 'border-gray-200 bg-white text-gray-600',
          className,
        )}
      >
        <LuClock className="size-5 shrink-0" />
        {formattedTime ?? placeholder}
      </DialogTrigger>
      <DialogContent
        className="top-1/2 -translate-y-1/2 mx-auto max-w-80 nsm:w-80"
        initialFocus={initialFocus}
      >
        <DialogTitle
          className={cn('text-center text-base', description ? 'mb-1' : 'mb-3')}
        >
          {title}
        </DialogTitle>
        {description && (
          <DialogDescription className="mb-3 text-center text-sm text-gray-600">
            {description}
          </DialogDescription>
        )}
        {panel}
      </DialogContent>
    </Dialog>
  );
}
